import { isEmpty } from 'lodash'
import { addTaxablePriceToCartItems, addPromoData } from '../../../helpers/cartItemsFormatter'
import { buildSelectedRatesArray } from '../ShippingMethodSelector/shippingGroupsHelpers'

/**
 * Creates FormData for tax calculation request
 * @param {object} params - Tax calculation parameters
 * @param {object} params.checkout - Checkout state object
 * @param {object} params.checkoutRates - Checkout rates state (for ShipEngine)
 * @param {Array} params.cartItems - Array of cart items
 * @param {object} params.currentUser - Current user object
 * @param {object} params.portal - Portal configuration
 * @param {object} params.managedCustomerAddresses - Managed customer addresses
 * @returns {FormData} - FormData object for tax calculation request
 */
export const createTaxFormData = ({ checkout, checkoutRates, cartItems, currentUser, portal, managedCustomerAddresses }) => {
  const formData = new FormData()
  const selectedAddress = checkout?.selectedCustomerAddress

  const cartItemsWithPromoData = addPromoData(cartItems)
  const cartItemsWithTaxablePrice = addTaxablePriceToCartItems(cartItemsWithPromoData)

  // Logged-in users: send address ID for backend lookup
  // Guests: send full address JSON (no DB record exists)
  if (currentUser?.id && selectedAddress?.id) {
    formData.append('customer_address_id', selectedAddress.id)
  } else if (!isEmpty(selectedAddress)) {
    formData.append('guest_address', JSON.stringify(selectedAddress))
  }

  formData.append('cart_items', JSON.stringify(cartItemsWithTaxablePrice))
  formData.append('tax_adjustment', checkout.taxReduction)

  // Handle shipping data based on integration type
  // ShipperHQ: uses checkout.checkoutData?.shipping (encrypted string)
  // ShipEngine: uses selectedRatesByGroup (array of rates with encrypted_cost)
  if (checkout.checkoutData?.shipping) {
    // ShipperHQ flow - send encrypted shipping string
    formData.append('shipment_in_cents', checkout.checkoutData?.shipping)
  } else if (checkoutRates?.selectedRatesByGroup) {
    // ShipEngine flow - send array of selected rates with encrypted costs
    const selectedRates = buildSelectedRatesArray(checkoutRates.selectedRatesByGroup)
    formData.append('selected_shipping_rates', JSON.stringify(selectedRates))
  }

  // if portal is multi address, send address selections
  if (portal.multiple_address_shipping && currentUser.id)
    formData.append('multi_address_selection', JSON.stringify(checkout.multiAddressSelection))

  if (checkout?.appliedPromotions.length > 0) {
    const updatedCart = checkout?.appliedPromotions[0].updated_cart
    const updatedCartWithTaxablePrice = updatedCart.map(cartItem => ({
      ...cartItem,
      taxable_price: cartItem.promo_data.discounted_price
    }))

    formData.append('promotion_id', checkout?.appliedPromotions[0].promotion_id)
    formData.append('subtotal_reduction', checkout?.subtotalReduction)
    formData.append('shipping_reduction', checkout?.shippingReduction)
    formData.append('shipping_discount_in_cents', checkout?.shippingReductionEncrypted)
    formData.append('product_reduction', checkout?.productReduction)
    formData.append('updated_cart', JSON.stringify(updatedCartWithTaxablePrice))
  }

  if (managedCustomerAddresses) formData.append('epcid_zero', managedCustomerAddresses.epcid)

  return formData
}

/**
 * Determines if tax calculation should be triggered
 * @param {object} params - Tax calculation conditions
 * @param {boolean} params.isInitializing - Whether checkout is initializing
 * @param {boolean} params.isCartLoaded - Whether cart is loaded
 * @param {Array} params.cartItems - Array of cart items
 * @param {object} params.checkout - Checkout state object
 * @param {object} params.portal - Portal configuration
 * @param {object} params.currentUser - Current user object
 * @returns {boolean} - Whether tax calculation should be triggered
 */
export const shouldCalculateTax = ({ isInitializing, isCartLoaded, cartItems, checkout, portal, currentUser }) => {
  // Don't calculate during initialization or if cart isn't loaded
  if (isInitializing || !isCartLoaded || cartItems?.length === 0) return false

  const selectedAddress = checkout?.selectedCustomerAddress

  // Require valid address with state field (needed for tax nexus check)
  if (isEmpty(selectedAddress) || !selectedAddress.state) return false

  const multiAddressShippingEnabled = portal.multiple_address_shipping && currentUser.id

  // For multi-address shipping, make sure each cart item has a selection
  const eachItemHasSelection = multiAddressShippingEnabled
    ? cartItems.every(cartItem => checkout.multiAddressSelection.map(s => s.cartItemId).includes(cartItem.cart_item_id))
    : true // for single-address shipping, we don't need this condition

  return multiAddressShippingEnabled ? eachItemHasSelection : true
}
